import { Modal } from "flowbite-react";
import { useToastContext } from "../../../../common/components/ui/toast/toast.context";
import { FormikProvider, useFormik } from "formik";
import { useEffect, useState } from "react";
import ButtonWithLoader from "../../../../common/components/ui/loading/ButtonLoading";
import InputFormik from "../../../../common/components/form/InputFormik";
import "./ModalCreatePurchaseOrder.scss";
import { getCostCenters } from "../../../../common/services/purchaseCostCenter.api";
import { IPurchaseCostCenter } from "../../../../types/purchaseCostCenter";
import ComboBoxFormik from "../../../../common/components/form/ComboBoxFormik";
import { getAllCategories } from "../../../../common/services/purchaseCategory.api";
import { IPurchaseCategory } from "../../../../types/purchaseCategory.type";
import { IPurchaseOrder, TYPE_PAYMENT } from "../../../../types/purchaseOrder.types";
import { getAllPurchaseRequestors } from "../../../../common/services/purchaseRequestor.api";
import { IPurchaseRequestor } from "../../../../types/purchaseRequestor.type";
import * as Yup from "yup";
import { maskCurrency } from "../../../../common/helpers/mask.helpers";
import { getAllSuppliers } from "../../../../common/services/supplier.api";
import { ISupplier } from "../../../../types/supplier.types";
import { getCompanies } from "../../../../common/services/company.api";
import { ICompanyAccess } from "../../../../types/companyaccess.type";
import { useUser } from "../../../../common/services/users.api";
import { updatePurchaseOrder } from "../../../../common/services/purchaseOrder.api";

import { Spinner } from "flowbite-react";

type Props = {
  onClose: (data: IPurchaseOrder) => void
  purchaseOrder: IPurchaseOrder
}

const createSchema = () => {

  const objValidationComponentsPiece = {
    code: Yup.string().required('O Código é obrigatório'),
    companyId: Yup.string().required('A Empresa é obrigatório'),
    financial: Yup.object().shape({
      costCenterId: Yup.string().required("Centro de Custo é obrigatório"),
      categoryId: Yup.string().required("Categoria é obrigatório"),
      expectedDate: Yup.string().required('Data da primeira parcela é obrigatório'),
      typePayment: Yup.string().required("Tipo de pagaemnto é obrigatório"),
      billetFiles: Yup.mixed().test(
        'valida-boleto',
        'Dados de pagamento incompleto',
        function (value) {
          const { typePayment } = this.parent; // Acessa o mesmo nível do objeto no schema
          if (typePayment === 'boleto' && !value) {
            return false; // Retorna inválido se for boleto e o arquivo não estiver presente
          }
          return true; // Caso contrário, passa na validação
        }
      ),
      transfer: Yup.object().shape({
        name: Yup.string().test(
          'validate-name',
          'Nome é obrigatório',
          function (value) {
            const { typePayment } = this.parent; // Acessa o mesmo nível do objeto no schema
            if (typePayment === 'transferencia' && !value) {
              return false; // Retorna inválido se for boleto e o arquivo não estiver presente
            }
            return true; // Caso contrário, passa na validação
          }
        ),
        agency: Yup.string().test(
          'validate-agency',
          'Agencia é obrigatório',
          function (value) {
            const { typePayment } = this.parent;
            if (typePayment === 'transferencia' && !value) {
              return false;
            }
            return true;
          }
        ),
        accountBank: Yup.string().test(
          'validate-accountBank',
          'Conta bancária é obrigatório',
          function (value) {
            const { typePayment } = this.parent;
            if (typePayment === 'transferencia' && !value) {
              return false;
            }
            return true;
          }
        )
      }),
      pixKey: Yup.string().test(
        'valida-pix',
        'Chave PIX é obrigatório',
        function (value) {
          const { typePayment } = this.parent;
          if (typePayment === 'pix' && !value) {
            return false;
          }
          return true;
        }
      ),
      checkNumber: Yup.string().test(
        'validate-checkNumber',
        'Nº Cheque é obrigatório',
        function (value) {
          const { typePayment } = this.parent;
          if (typePayment === 'cheque' && !value) {
            return false;
          }
          return true;
        }
      )
    }),
  };

  return Yup.object().shape(objValidationComponentsPiece);
}

export default function ModalEditTable(props: Props) {
  const { notifySuccess, notifyError, notifyInfo } = useToastContext();
  const [loading, setLoading] = useState(false);
  const handleCloseModal = (data = props.purchaseOrder) => { props.onClose && props.onClose(data) };

  const [costCenters, setCostCenters] = useState<IPurchaseCostCenter[]>([]);
  const [categories, setCategories] = useState<IPurchaseCategory[]>([]);
  const [requestors, setRequestors] = useState<IPurchaseRequestor[]>([]);
  const [suppliers, setSuppliers] = useState<ISupplier[]>([]);
  const [companies, setCompanies] = useState<ICompanyAccess[]>([]);
  const [isLoadingData, setIsLoadingData] = useState(true);

  useEffect(() => {
    async function loadCompanies() {
      const data = await getCompanies();
      setCompanies(data.companies);
    }
    loadCompanies();
  }, []);

  const initialValues = {
    ...props.purchaseOrder,
    financial: {
      ...props.purchaseOrder.financial,
      expectedDate: (props.purchaseOrder.financial.expectedDate as string).split('T')[0]
    }
  };
  const user = useUser();

  useEffect(() => {

    async function loadCostCenters() {
      const result = await getCostCenters();
      setCostCenters(result.data);
    }

    async function loadRequestors() {
      const result = await getAllPurchaseRequestors();
      setRequestors(result.data);
    }

    async function loadSuppliers() {
      const result = await getAllSuppliers()
      setSuppliers(result.data)
    }

    loadCostCenters();
    loadRequestors();
    loadSuppliers();
  }, []);

  useEffect(() => {
    async function loadInitialData() {
      try {
        const [costCentersResult, requestorsResult, suppliersResult] = await Promise.all([
          getCostCenters(),
          getAllPurchaseRequestors(),
          getAllSuppliers(),
        ]);

        setCostCenters(costCentersResult.data);
        setRequestors(requestorsResult.data);
        setSuppliers(suppliersResult.data);
      } catch (error) {
        notifyError("Erro ao carregar dados iniciais.");
      } finally {
        setIsLoadingData(false);
      }
    }

    loadInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  const verifyComplementaryPaymentType = (data: IPurchaseOrder) => {

    if (data.financial.typePayment != TYPE_PAYMENT.Transferencia) {
      delete data.financial.transfer
    }
    if (data.financial.typePayment != TYPE_PAYMENT.Cheque) {
      delete data.financial.checkNumber
    }
    if (data.financial.typePayment != TYPE_PAYMENT.Pix) {
      delete data.financial.pixKey
    }
  }

  const parseMoneyStringToNumber = (value) => {
    if (typeof value == 'number') {
      return value
    }
    if (!value) {
      return 0;
    }
    return parseFloat(value.replace("R$", "").replace(/\s/g, "").replace(".", "").replace(",", "."));
  }

  const setInstalmentFormik = (totalValue: number) => {

    const parcelValue = totalValue / props.purchaseOrder.financial.instalment.length;
    // dueDate: addMonths(formik.values.financial.expectedDate as Date, i)
    // new Date(formik.values[`expectedDate${i}`])
    const instalment = new Array(props.purchaseOrder.financial.instalment.length).fill(null).map((_, i) => {
      return {
        installmentNumber: `${i + 1}`,
        value: parseFloat(parcelValue.toFixed(2)),
        dueDate: props.purchaseOrder.financial.instalment[i].dueDate
      }
    });

    formik.values.financial.instalment = instalment;
  }

  const formik = useFormik({
    initialValues,
    onSubmit: async (data) => {
      setLoading(true);

      let totalValue = 0;
      data.itens.map(item => {
        totalValue += parseMoneyStringToNumber(item.value) * item.qtd;
      });

      totalValue += (parseMoneyStringToNumber(data.financial.valueOfLabor) + parseMoneyStringToNumber(data.financial.deliveryForecast))
        - parseMoneyStringToNumber(data.financial.discountAmount);

      data.financial.value = totalValue;
      setInstalmentFormik(totalValue);

      verifyComplementaryPaymentType(data);

      const requestor = requestors.find(requestor => requestor.employeeEmail == user.email);
      if (!requestor) {
        notifyInfo(`Usuário: ${user.email} não é solicitante de Ordem de Compra`);
      }
      const result = await updatePurchaseOrder(data);

      setLoading(false);
      if (!result.ok && result.message) {
        notifyError(result.message);
        return;
      }

      notifySuccess("Criado Ordem de Compra com Sucesso.");
      handleCloseModal(data);
    },
    validationSchema: createSchema()
  });

  useEffect(() => {

    async function loadCategories() {
      const costCenter = costCenters.find(costCenter => costCenter.costCenterId == formik.values.financial.costCenterId);
      const result = (await getAllCategories()).data.filter(cateogry => costCenter?.categoriesId.includes(cateogry._id));
      setCategories(result);
    }
    loadCategories();
  }, [costCenters, formik.values.financial.costCenterId]);

  if (isLoadingData) {
    return (
      <Modal show={true} onClose={() => handleCloseModal()} className="relative text-black">
        <Modal.Header>
          <h2>Carregando dados...</h2>
        </Modal.Header>
        <Modal.Body className="flex justify-center items-center min-h-[200px]">
          <Spinner size="xl" aria-label="Loading spinner" />
        </Modal.Body>
      </Modal>
    );
  }

  const companyValue = companies.find(company => company.companyId === props.purchaseOrder.companyId);
  return (
    <Modal
      className='relative text-black'
      show={true}
      onClose={() => handleCloseModal()}
    >
      <Modal.Header className='text-black dark:text-black'>
        <h2 className="text-black">Editar Ordem de Compra</h2>
      </Modal.Header>
      <Modal.Body className='text-black dark:text-black'>
        <form onSubmit={formik.handleSubmit}>
          <FormikProvider value={formik}>
            <ComboBoxFormik
              className="mt-2"
              name="companyId"
              options={[
                // ✅ Empresa atual com fallback
                ...(companyValue
                  ? [{
                    label: companyValue.companyName || `Empresa ${companyValue.companyId || 'Sem ID'}`,
                    value: companyValue.companyId || companyValue._id || ''
                  }]
                  : []
                ),
                // ✅ Outras empresas válidas
                ...companies
                  .filter(company =>
                    (company.companyName || company._id) && // ✅ Pelo menos um dos dois deve existir
                    company.companyId !== props.purchaseOrder.companyId
                  )
                  .map(company => ({
                    value: company._id || '',
                    label: company.companyName || `Empresa ${company._id || 'Sem nome'}`
                  }))
              ]}
              label="Selecione a Empresa:"
              required
            />

            <ComboBoxFormik
              className="mt-2"
              name="supplierId"
              onChange={value => formik.setFieldValue('supplierName', suppliers.find(supplier => supplier._id === value)?.companyName)}
              options={[
                { label: '', value: '' },
                ...suppliers
                  .slice() // cria uma cópia para não mutar o original
                  .sort((a, b) => a.companyName.localeCompare(b.companyName))
                  .map(supplier => ({ value: supplier._id, label: supplier.companyName }))
              ]}
              label="Selecione o Fornecedor:"
              required
            />

            <ComboBoxFormik
              className="mt-2"
              name="financial.costCenterId"
              options={[{ label: '', value: '' }, ...costCenters.map(costCenter => ({ value: costCenter.costCenterId, label: costCenter.description }))]}
              label="Selecione o Centro Custo:"
              onChange={value => {
                const costCenterName = costCenters.find(costCenter => costCenter.costCenterId === value)?.description;
                formik.setFieldValue('financial.costCenterName', costCenterName);
              }}
              required
            />

            <ComboBoxFormik
              className="mt-2"
              name="financial.categoryId"
              options={[
                { label: '', value: '' },
                ...categories
                  .slice() // cria uma cópia para não mutar o original
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map(category => ({ value: category.idNibo, label: category.name }))
              ]}
              label="Selecione a Categoria:"
              onChange={value => {
                const categoryName = categories.find(category => category.idNibo === value)?.name;
                formik.setFieldValue('financial.categoryName', categoryName);
              }}
              required
            />


            <InputFormik className="mt-2" label="Data de pagamento previsto:" name="financial.expectedDate" type="date" required></InputFormik>
            <ComboBoxFormik label="Tipo de pagamento" name="financial.typePayment"
              options={[
                { label: '', value: '' },
                { label: TYPE_PAYMENT.Boleto, value: TYPE_PAYMENT.Boleto },
                { label: TYPE_PAYMENT.Pix, value: TYPE_PAYMENT.Pix },
                { label: TYPE_PAYMENT.Transferencia, value: TYPE_PAYMENT.Transferencia },
                { label: TYPE_PAYMENT.Credito, value: TYPE_PAYMENT.Credito },
                { label: TYPE_PAYMENT.Dinheiro, value: TYPE_PAYMENT.Dinheiro },
                { label: TYPE_PAYMENT.Cheque, value: TYPE_PAYMENT.Cheque }
              ]}
            />
            {formik.values.financial.typePayment === 'transferencia' && (
              <div className="ml-4 mb-5">
                <h3 className="mb-4 text-transferencia">Dados do Beneficiário</h3>
                <InputFormik type="text" label="Nome completo:" name="financial.transfer.name" required />
                <InputFormik className="mt-2" label="Agência:" name="financial.transfer.agency" required />
                <InputFormik className="mt-2" type="text" label="Conta bancária:" name="financial.transfer.accountBank" required />
              </div>
            )}
            {formik.values.financial.typePayment === 'pix' && (
              <div className="ml-4 mb-5">
                <InputFormik type="text" label="Chave Pix:" name="financial.pixKey" required />
              </div>
            )}
            {formik.values.financial.typePayment === 'cheque' && (
              <div className="ml-4 mb-5">
                <InputFormik type="text" label="Nº Cheque:" name="financial.checkNumber" required />
              </div>
            )}
            <InputFormik className="mt-2" label="Valor de Desconto:" name="financial.discountAmount" mask={maskCurrency}></InputFormik>
            <InputFormik className="mt-2" label="Valor de Mão de Obra:" name="financial.valueOfLabor" mask={maskCurrency}></InputFormik>
            <InputFormik className="mt-2" label="Valor de Entrega:" name="financial.deliveryForecast" mask={maskCurrency}></InputFormik>

          </FormikProvider>
          <div className="containerButtonPurcahseOrder">
            <ButtonWithLoader type="submit" isLoading={loading} >Salvar</ButtonWithLoader>
          </div>
        </form>
      </Modal.Body>
    </Modal>
  );
}